/*=========================================================================================
  File Name: main.js
  Description: main vue(js) file
  ----------------------------------------------------------------------------------------
  Project: MISA InvoiceBots
  Author: MISA JSC
  Author URL: http://www.misa.com.vn
==========================================================================================*/
// __webpack_public_path__ = 'https://inbotappcdn.misa.local/';
import Vue from 'vue';
import App from './App.vue';
import * as Sentry from '@sentry/vue';
import EventBusGlobal from '@/sys/eventBusGlobal';

import 'vuesax/dist/vuesax.css'; // Vuesax

// Theme Configurations
import '../themeConfig.js';

//Add extension
import './common/prototype';

// Globally Registered Components
import './globalComponents.js';

// keycode
import './keycodes.js';

// Globally Registered Components
import './globalPlugin.js';

// Tailwind
import '@/assets/css/main.css';

// Vue Router
import router from './router';

// Vuex Store
import store from './store/store';

// i18n
import i18n from './i18n/i18n';

import './filters/filters';

// // PrismJS
import 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import './directives'; // Import tất cả directive từ file index.js

// Feather font icon
require('./assets/css/iconfont.css');
require('./assets/css/icon.css');
require('./assets/css/icon-new.css');
require('./assets/css/icon-new-v2.css');
require('./assets/css/Icon_sprites.css');
require('./assets/css/icon-notification.css');
require('./assets/css/process.css');

// KHÔNG require loader.css ở đây: public/index.html đã load tĩnh css/loader.css.
// Require lại sẽ khiến webpack emit thêm img/loading.<hash>.svg (dư 1 request).

// Styles: SCSS
import './assets/scss/main.scss';

// Styles: SCSS
import '@/assets/scss/common/main.scss';
import '@/assets/scss/common/common_default.scss';

import VueDraggable from 'vuedraggable';
Vue.use(VueDraggable);

import VTooltip from '@/components/msTooltip';
Vue.use(VTooltip);

import browserDetect from 'vue-browser-detect-plugin';
Vue.use(browserDetect);

Vue.config.productionTip = false;

// Vue.config.errorHandler = log.handleGlobalException;
if (process.env.NODE_ENV == 'development') {
	Vue.config.performance = true;
}

// Cấu hình theo dõi Perfromance và lỗi ứng dụng với Sentry
const sentryConfig = window.__env && window.__env.sentryConfig ? window.__env.sentryConfig : null;
if (sentryConfig && sentryConfig.enable) {
	const tracesSampleRate = sentryConfig.tracesSampleRate ? sentryConfig.tracesSampleRate : 0.01;
	//Cấu hình config các chỉ số Sentry
	const THRESHOLDS = sentryConfig.thresholds
		? sentryConfig.thresholds
		: {
				TRACE_SAMPLE_RATE: 0.01, // Tỷ lệ lấy mẫu vòng 1 (1%)
				LCP: 2500, // Ngưỡng tối ưu LCP (ms)
				FCP: 1800, // Ngưỡng tối ưu FCP (ms)
				GOOD_SPAN_KEEP_RATE: 0, // Tỷ lệ GIỮ LẠI SPAN CHI TIẾT của nhóm user nhanh (2%)
				BAD_SPAN_KEEP_RATE: 0.2
		  };
	//Cấu hình bật tắt ghi log để kiểm tra kết quả ghi log sentry
	const enabledTrace = sentryConfig.enabledTrace ? sentryConfig.enabledTrace : false;

	Sentry.init({
		Vue,
		dsn:
			sentryConfig.dsn ||
			'https://d9eadd50f9e558472b2bf5addc696e06@o4510509735411712.ingest.us.sentry.io/4510513624842240',
		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: true,
		integrations: [
			Sentry.browserTracingIntegration({
				router,
				enableLongTask: false,
				enableLongAnimationFrame: false,
				enableInp: false,
				idleTimeout: 1000
			})
		],
		tracesSampler: samplingContext => {
			const originAttr =
				samplingContext && samplingContext.attributes && samplingContext.attributes['sentry.origin'];
			const allowSample = originAttr && (originAttr.includes('pageload') || originAttr.includes('navigation'));
			const result = allowSample ? THRESHOLDS.TRACE_SAMPLE_RATE : 0.0;
			enabledTrace && console.log('Sentry.tracesSampler', originAttr, result, samplingContext);
			return result;
		},
		beforeSendTransaction(event) {
			const op = event && event.contexts && event.contexts.trace && event.contexts.trace.op;
			enabledTrace && console.log('Sentry.beforeSendTransaction', op, event);
			if (op === 'pageload' || op === 'navigation') {
				const measurements = event.measurements || {};

				const lcpValue = measurements['lcp'] ? measurements['lcp'].value : 0;
				const fcpValue = measurements['fcp'] ? measurements['fcp'].value : 0;

				// BƯỚC 1: LÀM SẠCH DASHBOARD (Chỉ hiển thị LCP và FCP)
				const cleanMeasurements = {};
				if (measurements.lcp) cleanMeasurements.lcp = measurements.lcp;
				if (measurements.fcp) cleanMeasurements.fcp = measurements.fcp;
				event.measurements = cleanMeasurements;

				// BƯỚC 2: KIỂM TRA ĐIỀU KIỆN USER CHẬM (VƯỢT NGƯỠNG LCP HOẶC FCP)
				if (lcpValue > THRESHOLDS.LCP || fcpValue > THRESHOLDS.FCP) {
					// USER CHẬM: Giữ nguyên 100% transaction và ghi lại 1 tỉ lệ span chi tiết để debug
					event.tags = { ...event.tags, 'performance.status': 'bad_metrics' };
					if (!THRESHOLDS.BAD_SPAN_KEEP_RATE) return event;
					if (THRESHOLDS.BAD_SPAN_KEEP_RATE > 0 && Math.random() < THRESHOLDS.BAD_SPAN_KEEP_RATE) {
						//Giữ nguyên toàn bộ span chi tiết phục vụ đối chiếu
						event.tags['performance.span_status'] = 'full_spans';
					} else {
						//Xóa sạch mảng span con để tiết kiệm count span
						event.spans = [];
					}

					return event;
				} else {
					// USER NHANH (ĐẠT CHUẨN): LUÔN LUÔN GIỮ TRANSACTION để Sentry tính toán P75/P90 chuẩn xác
					event.tags = { ...event.tags, 'performance.status': 'good_metrics' };
					// Xúc xắc ngẫu nhiên xem user nhanh này có thuộc diện 2% được giữ lại span hay không
					if (THRESHOLDS.GOOD_SPAN_KEEP_RATE > 0 && Math.random() < THRESHOLDS.GOOD_SPAN_KEEP_RATE) {
						// Giữ nguyên toàn bộ span chi tiết phục vụ đối chiếu
						event.tags['performance.span_status'] = 'full_spans';
					} else {
						// Xóa sạch mảng span con để tiết kiệm count span
						event.spans = [];
						event.tags['performance.span_status'] = 'cleared_spans';
					}
					return event;
				}
			}
			return null;
		}
	});
}

//Kiểm tra phiên bản và trình duyệt
let appVer = window.navigator.userAgent;
if (
	appVer.indexOf('Edge/') > -1 || //edge
	appVer.indexOf('.NET') > -1 || //ie
	(appVer.indexOf('Safari/') > -1 && appVer.indexOf('Version/') > -1 && appVer.indexOf('OPR/') == -1)
) {
	//Ứng dụng không hỗ trợ những trình duyệt này, nên không tạo instance của VueJs
} else {
	new Vue({
		router,
		store,
		i18n: i18n,
		render: h => h(App)
	}).$mount('#app');
	window.__eventBus = EventBusGlobal;
}
